import { ReactNode, useMemo } from 'react';

import { Typography } from 'antd';
import moment from 'moment';

import { CalendarGrid } from './components/calendarGrid';
import { MinutePicker } from './components/minutePicker';
import { WeekCarousel } from './components/weekCarousel';
import { WeekNavigator } from './components/weekNavigator';
import { TWorkSchedule, getWorkingHourRange, includeHours } from './helpers/workSchedule';
import { TDateRangeSelection, TDateRangeSelectionApi, TRangeEdge } from './hooks/useDateRangeSelection';
import { useWeekCarousel } from './hooks/useWeekCarousel';

import style from './dateRangeCalendar.module.scss';

/** Что получает шапка календаря: черновик выбора и задание края точным временем */
export type TDateRangeHeaderProps = TDateRangeSelection & {
    setEdge: (edge: TRangeEdge, value: moment.Moment) => void;
};

export type TDateRangeCalendarProps = {
    /**
     * Шапка над календарём («Запись с … по …»). Своя у каждого экрана, поэтому
     * календарь её не знает — только отдаёт текущий черновик выбора.
     */
    renderHeader?: (props: TDateRangeHeaderProps) => ReactNode;
    /** График компании: им подсвечиваются рабочие часы и ограничивается сетка по вертикали */
    schedule?: TWorkSchedule | null;
};

type TProps = TDateRangeCalendarProps & {
    /** Выбор живёт у владельца (`useDateRangeSelection`): ему же решать, когда сохранять */
    range: TDateRangeSelectionApi;
};

/**
 * Календарь выбора начала и окончания по часам — без кнопок, чтобы встраивать
 * и в модалку, и в шаг мастера.
 */
export const DateRangeCalendar = ({ range, renderHeader, schedule }: TProps) => {
    const { selection, initialSelection, pickCell, setMinutes, setEdge, isComplete, isValid } = range;
    const carousel = useWeekCarousel(initialSelection.start);
    const now = useMemo(() => moment(), []);

    // Сетку не сужаем по выбранным потом ячейкам — только по исходной записи,
    // иначе строки прыгали бы под пальцем
    const hours = useMemo(
        () =>
            includeHours(getWorkingHourRange(schedule), [
                initialSelection.start,
                initialSelection.end,
            ]),
        [schedule, initialSelection]
    );

    const activeValue = selection.activeEdge ? selection[selection.activeEdge] : null;

    return (
        <div className={style['calendar']}>
            {renderHeader?.({
                ...selection,
                // Край, выбранный не в сетке, может быть на другой неделе — показываем её
                setEdge: (edge, value) => {
                    setEdge(edge, value);
                    carousel.showWeekOf(value);
                },
            })}

            <WeekNavigator
                weekStart={carousel.weekStart}
                onPrev={carousel.prevWeek}
                onNext={carousel.nextWeek}
                onToday={carousel.toToday}
            />

            <WeekCarousel
                carousel={carousel}
                renderWeek={(weekStart) => (
                    <CalendarGrid
                        weekStart={weekStart}
                        hours={hours}
                        schedule={schedule}
                        selection={selection}
                        now={now}
                        onCellClick={pickCell}
                    />
                )}
            />

            {selection.activeEdge && activeValue && (
                <MinutePicker
                    edge={selection.activeEdge}
                    value={activeValue}
                    onChange={setMinutes}
                />
            )}

            {isComplete && !isValid && (
                <Typography.Text type="danger">Окончание должно быть позже начала</Typography.Text>
            )}
        </div>
    );
};
